// Core
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { FieldSearchSelect, LineChart, SectionCard, Segmented } from "components/AtomsV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectTimeSelector } from "state/application/applicationSlice";
import { selectAllMeters, useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";
import { useGetOpportunityCostsByMeterQuery } from "state/opportunity_costs/opportunityCostsApiSlice";

// Utils
import { DateTime } from "luxon";
import { formatDayMonthTime } from "utils/time";

function OpportunityCostsCharts({ meterId, setMeterId }) {
  const { t } = useTranslation(["opportunity_costs"]);
  const community = useSelector(selectCurrentCommunity);
  const { startDate, endDate } = useSelector(selectTimeSelector);
  const [typeFilter, setTypeFilter] = useState("platform");
  const isUpload = typeFilter === "user";

  const { meters } = useGetMetersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data: metersData }) => ({
        meters: metersData && selectAllMeters(metersData),
      }),
    }
  );

  const cpeMeters = useMemo(() => (meters ?? []).filter((m) => m.type === "CPE"), [meters]);
  const meterOptions = cpeMeters.map((m) => ({
    value: m.id,
    label: m.identifier ?? m.name ?? m.id,
  }));

  // Auto-select first CPE when meters load and none is selected yet.
  useEffect(() => {
    if (!meterId && cpeMeters.length > 0) {
      setMeterId(cpeMeters[0].id);
    }
  }, [cpeMeters.length]);

  const startDatetime = DateTime.fromISO(startDate).startOf("day").toISO();
  const endDatetime = DateTime.fromISO(endDate).endOf("day").toISO();

  const {
    data: ocData,
    isFetching,
    isError,
  } = useGetOpportunityCostsByMeterQuery(
    { communityId: community.id, meterId, startDatetime, endDatetime },
    { skip: !meterId }
  );

  const rows = useMemo(
    () => (ocData ?? []).filter((item) => item.is_upload === isUpload),
    [ocData, isUpload]
  );
  const hasData = !isFetching && !isError && rows.length > 0;

  // Two series (buy / sell) over a real datetime axis.
  const { series, timestamps, labels } = useMemo(() => {
    const ts = [...new Set(rows.map((d) => d.datetime_timestamp))].sort();
    const tsIndex = new Map(ts.map((value, i) => [value, i]));
    const buy = new Array(ts.length).fill(null);
    const sell = new Array(ts.length).fill(null);
    rows.forEach((d) => {
      const i = tsIndex.get(d.datetime_timestamp);
      if (i == null) return;
      buy[i] = d.l_buy != null ? +d.l_buy.toFixed(4) : null;
      sell[i] = d.l_sell != null ? +d.l_sell.toFixed(4) : null;
    });
    return {
      series: [
        { name: t("table.columns.l_buy"), data: buy, color: "var(--accent)" },
        { name: t("table.columns.l_sell"), data: sell, color: "var(--warm)" },
      ],
      timestamps: ts,
      labels: ts.map((value) => formatDayMonthTime(DateTime.fromISO(value))),
    };
  }, [rows, t]);

  const action = (
    <div className="flex items-center gap-[10px] flex-wrap">
      <Segmented
        value={typeFilter}
        onChange={setTypeFilter}
        options={[
          { value: "platform", label: t("table.type.calculated_by_platform") },
          { value: "user", label: t("table.type.uploaded_by_user") },
        ]}
      />
      <div className="min-w-[220px]">
        <FieldSearchSelect options={meterOptions} value={meterId ?? ""} onChange={setMeterId} />
      </div>
    </div>
  );

  // eslint-disable-next-line no-nested-ternary
  const emptyText = isFetching ? "…" : meterId ? t("charts.no_data") : t("charts.select_cpe");

  return (
    <div className="flex flex-col gap-rec">
      <SectionCard id="oc-line" icon="show_chart" title={t("charts.title")} action={action}>
        <div className="flex items-center gap-[14px] flex-wrap mb-2 text-[12px] text-ink-2">
          <span className="inline-flex items-center gap-[6px]">
            <i
              className="inline-block w-[9px] h-[9px] rounded-[3px]"
              style={{ background: "var(--accent)" }}
            />
            {t("table.columns.l_buy")}
          </span>
          <span className="inline-flex items-center gap-[6px]">
            <i
              className="inline-block w-[9px] h-[9px] rounded-[3px]"
              style={{ background: "var(--warm)" }}
            />
            {t("table.columns.l_sell")}
          </span>
        </div>
        {hasData ? (
          <LineChart
            series={series}
            labels={labels}
            timestamps={timestamps}
            unit="€"
            height={340}
            type="area"
            curve="stepline"
            yTitle="€/kWh"
            showLegend={false}
          />
        ) : (
          <div className="py-10 text-center text-[13px] text-ink-3">{emptyText}</div>
        )}
      </SectionCard>
    </div>
  );
}

OpportunityCostsCharts.propTypes = {
  meterId: PropTypes.string.isRequired,
  setMeterId: PropTypes.func.isRequired,
};

export default OpportunityCostsCharts;
